import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LocalizationStatus } from 'libs.localization.common';
import { useStudyL10nMap } from 'libs.localization.react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { LocaleStatusProgress, } from './locale_status_progress.component';
import { TranslationWorkbenchContext } from '../../contexts';
export const StudyLocaleStatusProgress = (props) => {
    const { studyId } = useParams();
    const { setStudyL10nMap } = useStudyL10nMap();
    const { study, updateStudy } = useContext(TranslationWorkbenchContext);
    const { addNotification } = useToastNotification();
    const t = useLocalizeMessage();
    // Use refs to avoid recreating handler when study object reference changes
    // Only recreate when the actual value we care about (l10nMapResourceId) changes
    const studyRef = useRef(study);
    const updateStudyRef = useRef(updateStudy);
    useEffect(() => {
        studyRef.current = study;
        updateStudyRef.current = updateStudy;
    }, [study, updateStudy]);
    // Legacy frontend sync of the study l10n map. Used only for activities becoming
    // READY: they publish through the instrument-bundler path, which does not update the
    // study l10n map. Every other asset is synced server-side by `/study/asset_status`.
    const syncStudyL10nMapFromFrontend = useCallback((id, locale, version) => {
        if (!studyId) {
            return;
        }
        const currentL10nMapId = studyRef.current.l10nMapResourceId;
        setStudyL10nMap({ studyId: parseInt(studyId, 10), currentL10nMapId, assetLocaleVersionSet: [{ id, locale, version }] }, {
            onSuccess: (response) => {
                if (response?.data?.id) {
                    studyRef.current.l10nMapResourceId = response.data.id;
                    updateStudyRef.current?.(studyRef.current);
                }
            },
            onError: (error) => {
                console.error('Failed to update study l10n map:', error);
                addNotification({
                    title: t('Failed to update translation status. Please try again.'),
                    type: 'error',
                });
            },
        });
    }, [studyId, setStudyL10nMap, addNotification, t]);
    const handler = useCallback((props) => {
        if (!studyId) {
            console.error(new Error('Internal error'));
            return;
        }
        const { id, locale, version, status, syncedByBackend, l10nMapResourceId } = props;
        // Non-activity assets go through the v2 `/study/asset_status` endpoint, which
        // publishes the l10n map, updates the study config, and returns the published id.
        // The backend is authoritative here: on success the id is guaranteed, so refresh
        // from it. A missing id means the orchestration violated its contract — surface an
        // error rather than racing a frontend re-publish.
        if (syncedByBackend) {
            if (l10nMapResourceId) {
                const currentStudy = studyRef.current;
                const nextStudy = Object.assign(Object.create(Object.getPrototypeOf(currentStudy)), currentStudy);
                nextStudy.l10nMapResourceId = l10nMapResourceId;
                updateStudyRef.current?.(nextStudy);
            }
            else {
                console.error(new Error('study/asset_status succeeded without an l10nMapResourceId'));
                addNotification({
                    title: t('Failed to update translation status. Please try again.'),
                    type: 'error',
                });
            }
            return;
        }
        // The study l10n map only changes when an asset becomes READY; other transitions
        // (In Progress, In Review, Not Started) don't touch it, so there's nothing to sync.
        if (status !== LocalizationStatus.READY) {
            return;
        }
        // Only activities reach READY without a backend sync (instrument-bundler path).
        // Until that publish moves server-side, activities sync the study map here.
        syncStudyL10nMapFromFrontend(id, locale, version);
    }, [studyId, syncStudyL10nMapFromFrontend, addNotification, t]);
    if (!studyId) {
        return null;
    }
    return _jsx(LocaleStatusProgress, { ...props, onStatusChange: handler });
};
